import { createContext, PropsWithChildren, useCallback, useContext, useState } from "react";
import { api } from "../../contracts";
import { Chapter, RateModel, Topic } from "../../contracts/chapter/dto";
import { useCourse } from "../course";

type ContextChapterData = {
    getChapter: (id: number) => Promise<Chapter>,
    chapter?: Chapter,
    chapters: Chapter[];
    getChapters: () => Promise<Chapter[]>;
    topics: Topic[],
    getTopics: () => Promise<Topic[]>;
    sendRate: (rate: RateModel) => Promise<any>;
};

const DEFAULT: ContextChapterData = {
    getChapter: () => Promise.resolve({} as Chapter),
    chapter: undefined,
    chapters: [],
    getChapters: () => ({} as any),
    topics: [],
    getTopics: () => [] as any,
    sendRate: (rate: RateModel) => Promise.resolve({} as Chapter),
};

const ChapterContext = createContext(DEFAULT);

export const ChapterProvder = ({ children }: PropsWithChildren<any>) => {
    const { type } = useCourse();

    const [chapters, setChapters] = useState<Chapter[]>([]);
    const [currentChapter, setCurrentChapter] = useState<Chapter>();
    const [topics, setTopics] = useState<Topic[]>([]);

    const getChapters = useCallback(async () => {
        const response = await api.chapter.getChapters(type);
        setChapters(response);
        return response;
    }, [type]);

    const getTopics = useCallback(async () => {
        const response = await api.chapter.getTopics(type);
        setTopics(response);

        return response;
    }, [type]);

    const getChapter = useCallback(async (id: number) => {
        const response = await api.chapter.getChapter(id, type);
        setCurrentChapter(response);

        return response;
    }, [type]);

    const sendRate = useCallback(async (rate: RateModel): Promise<any> => {
        const response = await api.chapter.postRate(rate, type);

        return response;
    }, [type]);

    return <ChapterContext.Provider
        value={{
            getChapter,
            chapter: currentChapter,
            chapters,
            getChapters,
            getTopics,
            topics,
            sendRate
        }}>
        {children}
    </ChapterContext.Provider>
}

export function useChapter(): ContextChapterData {
    const context = useContext(ChapterContext);

    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }

    return context;
}